import React, { useEffect, useState } from "react";
import AdminLayout from "../../LayoutWrapper/AdminLayout";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { postApihandler } from "../../ApiHandler";
import { useAuth } from "../../context/AuthContext";
import { USER_ROLE, getDriverOnboardingPath } from "../../utils/auth_storage";
import swal from "sweetalert";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import "../../CSS/UserLogin/index.css";

const DriverLogin = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login, isAuthenticated, isDriver, session } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { register, handleSubmit } = useForm();

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    if (isDriver) {
      navigate(getDriverOnboardingPath(session?.profile), { replace: true });
      return;
    }
    navigate("/", { replace: true });
  }, [navigate, isAuthenticated, isDriver, session?.profile]);

  const submitLogin = async (value) => {
    setIsSubmitting(true);
    try {
      const result = await postApihandler("/driverLogin", value);
      if (result.status === 200) {
        login(result.data, USER_ROLE.DRIVER);
        swal({
          icon: "success",
          text: "You Have Successfully Logined",
        });
        navigate("/personaldocument");
      } else {
        swal({
          icon: "error",
          title: "Please Try Again",
          text: result.error.response.data.message,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <AdminLayout>
      <div className="user-login-wrapper">
        <section className="user-login-page">
          <div className="user-login-hero">
            <span className="user-login-blob user-login-blob--1" aria-hidden="true" />
            <span className="user-login-blob user-login-blob--2" aria-hidden="true" />
            <span className="user-login-blob user-login-blob--3" aria-hidden="true" />
            <div className="user-login-hero-content">
              <span className="user-login-hero-badge">
                <DirectionsCarFilledOutlinedIcon sx={{ fontSize: 18 }} />
                Comfort Trip Driver
              </span>
              <h2 className="user-login-hero-title">Welcome back, driver</h2>
              <p className="user-login-hero-subtitle">
                Sign in to manage your profile, documents, and trips with Comfort Trip.
              </p>
            </div>
          </div>

          <div className="user-login-panel">
            <div className="user-login-card">
              <header className="user-login-card-header">
                <h1 className="user-login-card-title">{t("page_title_driverLogin")}</h1>
                <p className="user-login-card-subtitle">
                  Enter your driver credentials to continue
                </p>
              </header>

              <form
                className="user-login-form"
                noValidate
                onSubmit={handleSubmit(submitLogin)}
              >
                <div className="user-login-field">
                  <label className="user-login-label" htmlFor="driver-login-email">
                    {t("form_email_text")}
                  </label>
                  <div className="user-login-input-wrap">
                    <EmailOutlinedIcon className="user-login-input-icon" />
                    <input
                      id="driver-login-email"
                      className="user-login-input"
                      type="email"
                      placeholder={t("form_email_text")}
                      autoComplete="email"
                      required
                      {...register("email")}
                    />
                  </div>
                </div>

                <div className="user-login-field">
                  <label className="user-login-label" htmlFor="driver-login-password">
                    {t("form_password_text")}
                  </label>
                  <div className="user-login-input-wrap">
                    <LockOutlinedIcon className="user-login-input-icon" />
                    <input
                      id="driver-login-password"
                      className="user-login-input user-login-input--password"
                      type={showPassword ? "text" : "password"}
                      placeholder={t("form_password_text")}
                      autoComplete="current-password"
                      required
                      {...register("password")}
                    />
                    <button
                      type="button"
                      className="user-login-toggle-password"
                      onClick={togglePasswordVisibility}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? (
                        <VisibilityOffOutlinedIcon fontSize="small" />
                      ) : (
                        <VisibilityOutlinedIcon fontSize="small" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="user-login-forgot">
                  <Link to="/driver-reset-password">{t("form_forgotPass")}</Link>
                </div>

                <button
                  type="submit"
                  className="user-login-submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting && <span className="user-login-submit-spinner" />}
                  {t("form_loginBtn_text")}
                </button>
              </form>

              <footer className="user-login-footer">
                <p>
                  {t("form_dont_have_account")}
                  <Link to="/driver-signup">{t("form_signupBtn_text")}</Link>
                </p>
              </footer>
            </div>
          </div>
        </section>
      </div>
    </AdminLayout>
  );
};

export default DriverLogin;
